import { api } from '@/lib/api';
import type { MessageId } from '@/i18n/messages';
import type { SectionKind } from '@/components/Rafif';
import type { StudentAssignment, StudentClasses, StudentInbox, StudentMessage, StudentSchool, StudentTimetable } from '@/lib/types';

/**
 * استعلامات بوابة الطالب (الإصدار الثاني): كل مسارات `/me/student/*` بلا سياق مؤسسة —
 * الخادم يشتقّ المؤسسة من حساب الطالب نفسه. المفاتيح تحت `['me','student', …]` فتُبطَل معاً.
 */
const O = { institutionId: null } as const;

export const schoolQuery = () => ({ queryKey: ['me', 'student', 'school'] as const, queryFn: () => api.get<StudentSchool>('/me/student/school', O) });
export const classesQuery = () => ({ queryKey: ['me', 'student', 'classes'] as const, queryFn: () => api.get<StudentClasses>('/me/student/classes', O), refetchInterval: 60_000 });
export const messagesQuery = () => ({ queryKey: ['me', 'student', 'messages'] as const, queryFn: () => api.get<{ items: StudentMessage[] }>('/me/student/messages', O) });
export const assignmentsQuery = () => ({ queryKey: ['me', 'student', 'assignments'] as const, queryFn: () => api.get<{ items: StudentAssignment[] }>('/me/student/assignments', O) });
/** جدولُ حصص الطالب (0070) — صفُّه النشط وحصصُه؛ `classroom: null` لمن لا صفَّ له */
export const timetableQuery = () => ({ queryKey: ['me', 'student', 'timetable'] as const, queryFn: () => api.get<StudentTimetable>('/me/student/timetable', O) });
export const inboxQuery = () => ({ queryKey: ['me', 'student', 'inbox'] as const, queryFn: () => api.get<StudentInbox>('/me/student/inbox', O), refetchInterval: 60_000 });

export const studentApi = {
  watch: (broadcastId: string) => api.post(`/me/student/classes/${broadcastId}/watch`, undefined, O),
  sendMessage: (toUserId: string, bodyAr: string) => api.post<StudentMessage>('/me/student/messages', { toUserId, bodyAr }, O),
  readMessage: (id: string) => api.post(`/me/student/messages/${id}/read`, undefined, O),
  submit: (id: string, body: { noteAr?: string; playId?: string }) => api.post<StudentAssignment>(`/me/student/assignments/${id}/submit`, body, O),
  /** 0072 — الحلُّ ملفاً (PDF أو صورة) مع ملاحظةٍ اختيارية */
  submitFile: (id: string, file: File, noteAr?: string) => api.upload<StudentAssignment>(`/me/student/assignments/${id}/submit-file`, file, noteAr ? { noteAr } : {}, O),
  readNotification: (id: string) => api.post(`/me/student/notifications/${id}/read`, undefined, O),
};

/* ── الوقت بتوقيت عمّان — الطفل يقرأ ساعةً ويوماً لا طابعاً زمنياً ── */
const AMMAN = 'Asia/Amman';
const timeFmt = new Intl.DateTimeFormat('ar-u-nu-latn', { timeZone: AMMAN, hour: '2-digit', minute: '2-digit', hour12: false });
const dayFmt = new Intl.DateTimeFormat('ar-u-nu-latn', { timeZone: AMMAN, weekday: 'long' });
const dateFmt = new Intl.DateTimeFormat('ar-u-nu-latn-ca-gregory', { timeZone: AMMAN, day: 'numeric', month: 'long' });

export function ammanTime(iso: string): string { const d = new Date(iso); return Number.isNaN(d.getTime()) ? '—' : timeFmt.format(d); }
export function ammanDay(iso: string): string { const d = new Date(iso); return Number.isNaN(d.getTime()) ? '—' : dayFmt.format(d); }
export function ammanDate(iso: string): string { const d = new Date(iso); return Number.isNaN(d.getTime()) ? '—' : dateFmt.format(d); }
/** «اليوم» أو «غداً» أو اسم اليوم — كما يقولها الطفل */
export function relativeDay(iso: string, now = new Date()): string {
  const key = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: AMMAN, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  const target = key(new Date(iso));
  if (target === key(now)) return 'اليوم';
  if (target === key(new Date(now.getTime() + 86_400_000))) return 'غداً';
  return ammanDay(iso);
}

/* ── مربّعات القائمة — عناوين فقط بلا شرح (docs/student-portal-v2.md §2) ── */
export interface KidTile { to: string; labelId: MessageId; art: SectionKind; end?: boolean; counseling?: boolean; /** للحسابات التي تملك التمارين — لا للطالب العادي (0067) */ exercises?: boolean }
/**
 * ‹22 سبتمبر 2026› لا قائمةَ جانبية ولا مربّعاتٍ مكرّرة: الصفحةُ الأولى هي القائمة —
 * «مدرستي» (وفيها الفصل الإلكتروني وواجباتي والوارد) و«تماريني»، وللمُرشَد تقاريره وتوصياته.
 */
export const KID_TILES: readonly KidTile[] = [
  { to: '/me/student/school', labelId: 'kid.nav.school', art: 'school' },
  { to: '/me/student/games', labelId: 'kid.nav.games', art: 'games', exercises: true },
  { to: '/me/reports', labelId: 'kid.nav.reports', art: 'reports', counseling: true },
  { to: '/me/recommendations', labelId: 'kid.nav.recommendations', art: 'recommendations', counseling: true },
];
/** أقسامُ «مدرستي» — بطاقاتٌ داخل الصفحة لا بنودُ قائمة */
export const SCHOOL_TILES: readonly KidTile[] = [
  { to: '/me/student/class', labelId: 'kid.nav.class', art: 'class' },
  { to: '/me/student/homework', labelId: 'kid.nav.homework', art: 'homework' },
  { to: '/me/student/inbox', labelId: 'kid.nav.inbox', art: 'inbox' },
  /** 0072 — بطاقةُ الفصل بعد نشر النتائج؛ للطالب العادي وطالب التعلّم لا للإرشاد وحده */
  { to: '/me/student/grades', labelId: 'kid.nav.grades', art: 'grades' },
];
